import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Lock, ArrowLeft, Loader2, CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react';
import PasswordStrengthMeter from '../components/PasswordStrengthMeter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) setError('Invalid or missing reset token. Please request a new password reset.');
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }

    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(true);
      } else {
        setError(data.detail || 'Failed to reset password');
      }
    } catch {
      setError('Failed to connect. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FFF8F0' }}>
      <div className="w-full max-w-md p-8">
        <div className="text-center mb-8">
          <img src="/hibff-logo.png" alt="HiBFF" className="mx-auto mb-4" style={{ height: 96, width: 'auto' }} />
          <h1 className="text-2xl font-bold" style={{ color: '#1A1209' }}>Set New Password</h1>
        </div>

        {success ? (
          <div className="bg-white rounded-xl p-8 text-center" style={{ border: '1px solid #E0E0E0' }} data-testid="reset-success-message">
            <CheckCircle size={48} className="mx-auto mb-4" style={{ color: '#2EAA5C' }} />
            <h2 className="text-lg font-bold mb-2" style={{ color: '#1A1209' }}>Password Reset Complete</h2>
            <p className="text-sm mb-6" style={{ color: '#666' }}>Your password has been updated. You can now log in with your new password.</p>
            <Link to="/login" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-white text-sm font-medium" style={{ backgroundColor: '#F5831F' }} data-testid="goto-login-btn">
              <ArrowLeft size={16} /> Go to Login
            </Link>
          </div>
        ) : !token ? (
          <div className="bg-white rounded-xl p-8 text-center" style={{ border: '1px solid #E0E0E0' }}>
            <XCircle size={48} className="mx-auto mb-4" style={{ color: '#DC2626' }} />
            <h2 className="text-lg font-bold mb-2" style={{ color: '#1A1209' }}>Invalid Link</h2>
            <p className="text-sm mb-6" style={{ color: '#666' }}>{error}</p>
            <Link to="/forgot-password" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-white text-sm font-medium" style={{ backgroundColor: '#F5831F' }}>
              Request New Reset
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white rounded-xl p-8" style={{ border: '1px solid #E0E0E0' }} data-testid="reset-password-form">
            {error && <div className="mb-4 p-3 rounded-lg text-sm bg-red-50 text-red-700" data-testid="reset-error">{error}</div>}
            <div className="mb-4">
              <label className="text-sm font-medium mb-1.5 block" style={{ color: '#333' }}>New Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#999' }} />
                <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="At least 6 characters" required className="w-full pl-10 pr-11 py-3 rounded-lg border text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="reset-password-input" />
                <button type="button" onClick={() => setShowPassword(s => !s)} aria-label={showPassword ? 'Hide password' : 'Show password'} data-testid="reset-password-toggle-btn" tabIndex={-1} className="absolute right-3 top-3.5 p-0.5 text-gray-400 hover:text-gray-600">
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <PasswordStrengthMeter password={password} testIdPrefix="reset-pwd-strength" />
            </div>
            <div className="mb-5">
              <label className="text-sm font-medium mb-1.5 block" style={{ color: '#333' }}>Confirm Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#999' }} />
                <input type={showConfirmPassword ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirm your password" required className="w-full pl-10 pr-11 py-3 rounded-lg border text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="reset-confirm-input" />
                <button type="button" onClick={() => setShowConfirmPassword(s => !s)} aria-label={showConfirmPassword ? 'Hide password' : 'Show password'} data-testid="reset-confirm-toggle-btn" tabIndex={-1} className="absolute right-3 top-3.5 p-0.5 text-gray-400 hover:text-gray-600">
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading || !password || !confirmPassword} className="w-full py-3 rounded-lg text-white text-sm font-medium disabled:opacity-50" style={{ backgroundColor: '#F5831F' }} data-testid="reset-submit-btn">
              {loading ? <Loader2 size={16} className="animate-spin mx-auto" /> : 'Reset Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
